import React, { useState, useEffect } from 'react';
import Layout from '../Layout';
import {
    Calendar,
    CheckCircle,
    XCircle,
    TrendingUp,
    TrendingDown,
    Filter,
    Download,
    Eye,
    Clock,
    User,
    BookOpen,
    BarChart3,
    PieChart,
    CalendarDays,
    AlertCircle,
    Loader,
    RefreshCw,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { API_CONFIG } from '../config/api';

const MyAttendance = () => {
    // State management
    const [attendanceData, setAttendanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [classesData, setClassesData] = useState([]);

    // Filter state
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        classId: ''
    });

    // View state
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'list', 'summary', 'trends'
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

    useEffect(() => {
        fetchAttendanceData();
        fetchClassesData();
    }, [filters]);

    const fetchAttendanceData = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (filters.startDate) params.append('start_date', filters.startDate);
            if (filters.endDate) params.append('end_date', filters.endDate);
            if (filters.classId) params.append('class_id', filters.classId);

            const response = await fetch(`${API_CONFIG.BASE_URL}/student/my-attendance?${params}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAttendanceData(data);
        } catch (err) {
            console.error("Error fetching attendance data:", err);
            setError("Failed to load attendance data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchClassesData = async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/student/classes-with-attendance`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setClassesData(data.classes || []);
            }
        } catch (err) {
            console.error("Error fetching classes data:", err);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({ startDate: '', endDate: '', classId: '' });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getAttendanceColor = (percentage) => {
        if (percentage >= 90) return 'text-green-600 bg-green-100';
        if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
        return 'text-red-600 bg-red-100';
    };

    const getAttendanceIcon = (percentage) => {
        if (percentage >= 90) return <TrendingUp className="w-5 h-5" />;
        if (percentage >= 75) return <TrendingUp className="w-5 h-5" />;
        return <TrendingDown className="w-5 h-5" />;
    };

    const navigateMonth = (direction) => {
        const currentDate = new Date(selectedMonth + '-01');
        if (direction === 'prev') {
            currentDate.setMonth(currentDate.getMonth() - 1);
        } else {
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
        setSelectedMonth(currentDate.toISOString().slice(0, 7));
    };

    const renderCalendarView = () => {
        if (!attendanceData) return null;

        // Group records by date
        const recordsByDate = {};
        attendanceData.records.forEach(record => {
            recordsByDate[record.date] = record;
        });

        // Generate calendar for selected month
        const [year, month] = selectedMonth.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const calendarDays = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            calendarDays.push(<div key={`empty-${i}`} className="p-2"></div>);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const record = recordsByDate[dateStr];
            const isToday = dateStr === new Date().toISOString().split('T')[0];

            calendarDays.push(
                <div
                    key={day}
                    className={`p-2 border border-gray-200 min-h-[60px] relative ${isToday ? 'bg-blue-50 border-blue-300' : ''
                        }`}
                >
                    <div className="text-sm font-medium text-gray-700 mb-1">{day}</div>
                    {record && (
                        <div className={`absolute bottom-1 right-1 w-6 h-6 rounded-full flex items-center justify-center ${record.present ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                            {record.present ? (
                                <CheckCircle className="w-4 h-4 text-white" />
                            ) : (
                                <XCircle className="w-4 h-4 text-white" />
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 font-[poppins]">
                        Attendance Calendar
                    </h3>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigateMonth('prev')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-lg font-semibold font-[poppins] min-w-[150px] text-center">
                            {new Date(selectedMonth + '-01').toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long'
                            })}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Calendar Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-2 text-center font-semibold text-gray-600 text-sm">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                    {calendarDays}
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 font-[poppins]">Present</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 font-[poppins]">Absent</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-100 border-2 border-blue-300 rounded"></div>
                        <span className="text-sm text-gray-600 font-[poppins]">Today</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderListView = () => {
        if (!attendanceData) return null;

        return (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-xl font-bold text-gray-900 font-[poppins]">
                        Attendance Records
                    </h3>
                </div>
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                    {attendanceData.records.map((record, index) => (
                        <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${record.present ? 'bg-green-100' : 'bg-red-100'
                                        }`}>
                                        {record.present ? (
                                            <CheckCircle className="w-6 h-6 text-green-600" />
                                        ) : (
                                            <XCircle className="w-6 h-6 text-red-600" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900 font-[poppins]">
                                            {formatDate(record.date)}
                                        </div>
                                        <div className="text-sm text-gray-600 font-[poppins]">
                                            {record.class_name}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${record.present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                        {record.present ? 'Present' : 'Absent'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 font-[poppins]">
                                        Teacher: {record.teacher_name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {attendanceData.records.length === 0 && (
                    <div className="p-12 text-center">
                        <CalendarDays className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium text-gray-500 font-[poppins]">No attendance records found</p>
                        <p className="text-sm text-gray-400 font-[poppins]">
                            {filters.startDate || filters.endDate || filters.classId ?
                                "Try adjusting your filters to find records." :
                                "Your attendance records will appear here once teachers start marking attendance."}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderSummaryView = () => {
        if (!attendanceData) return null;

        const { summary, monthly_breakdown } = attendanceData;

        return (
            <div className="space-y-6">
                {/* Overall Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                <CalendarDays className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-gray-900 font-[poppins]">
                                    {summary.total_days}
                                </div>
                                <div className="text-sm text-gray-600 font-[poppins]">Total Days</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-gray-900 font-[poppins]">
                                    {summary.present_days}
                                </div>
                                <div className="text-sm text-gray-600 font-[poppins]">Present Days</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                                <XCircle className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-gray-900 font-[poppins]">
                                    {summary.absent_days}
                                </div>
                                <div className="text-sm text-gray-600 font-[poppins]">Absent Days</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summary.attendance_percentage >= 90 ? 'bg-green-100' :
                                summary.attendance_percentage >= 75 ? 'bg-yellow-100' : 'bg-red-100'
                                }`}>
                                {getAttendanceIcon(summary.attendance_percentage)}
                            </div>
                            <div>
                                <div className={`text-2xl font-bold font-[poppins] ${summary.attendance_percentage >= 90 ? 'text-green-600' :
                                    summary.attendance_percentage >= 75 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                    {summary.attendance_percentage}%
                                </div>
                                <div className="text-sm text-gray-600 font-[poppins]">Overall</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Performance */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <h3 className="text-xl font-bold text-gray-900 font-[poppins] mb-6">Recent Performance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-blue-900 font-[poppins]">Current Month</span>
                                <span className={`text-lg font-bold font-[poppins] ${summary.current_month_attendance >= 90 ? 'text-green-600' :
                                    summary.current_month_attendance >= 75 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                    {summary.current_month_attendance}%
                                </span>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.min(summary.current_month_attendance, 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="bg-purple-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-purple-900 font-[poppins]">Last 30 Days</span>
                                <span className={`text-lg font-bold font-[poppins] ${summary.last_30_days_attendance >= 90 ? 'text-green-600' :
                                    summary.last_30_days_attendance >= 75 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                    {summary.last_30_days_attendance}%
                                </span>
                            </div>
                            <div className="w-full bg-purple-200 rounded-full h-2">
                                <div
                                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${Math.min(summary.last_30_days_attendance, 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <h3 className="text-xl font-bold text-gray-900 font-[poppins] mb-6">Monthly Breakdown</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(monthly_breakdown).map(([month, data]) => (
                            <div key={month} className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="font-semibold text-gray-800 font-[poppins]">
                                        {new Date(month + '-01').toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long'
                                        })}
                                    </span>
                                    <span className={`text-sm font-bold font-[poppins] ${data.percentage >= 90 ? 'text-green-600' :
                                        data.percentage >= 75 ? 'text-yellow-600' : 'text-red-600'
                                        }`}>
                                        {data.percentage.toFixed(1)}%
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Present:</span>
                                        <span className="font-medium text-green-600">{data.present}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Absent:</span>
                                        <span className="font-medium text-red-600">{data.absent}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total:</span>
                                        <span className="font-medium">{data.total}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Classes Overview */}
                {classesData.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h3 className="text-xl font-bold text-gray-900 font-[poppins] mb-6">Classes Overview</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {classesData.map((cls, index) => (
                                <div key={index} className="border border-gray-200 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h4 className="font-semibold text-gray-900 font-[poppins]">{cls.class_name}</h4>
                                            <p className="text-sm text-gray-600 font-[poppins]">Teacher: {cls.teacher_name}</p>
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getAttendanceColor(cls.attendance_percentage)
                                            }`}>
                                            {cls.attendance_percentage}%
                                        </div>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Present:</span>
                                            <span className="font-medium text-green-600">{cls.present_days}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Absent:</span>
                                            <span className="font-medium text-red-600">{cls.absent_days}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total:</span>
                                            <span className="font-medium">{cls.total_days}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-[#D67C2C]" />
                        <p className="text-gray-600 font-[poppins]">Loading your attendance data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                        <h2 className="text-xl font-bold text-gray-900 mb-2 font-[poppins]">Error Loading Attendance</h2>
                        <p className="text-gray-600 mb-4 font-[poppins]">{error}</p>
                        <button
                            onClick={fetchAttendanceData}
                            className="bg-[#D67C2C] text-white px-6 py-2 rounded-lg hover:bg-[#B86A24] transition-colors font-[poppins]"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 font-[poppins]">My Attendance</h1>
                                <p className="text-gray-600 mt-2 font-[poppins]">
                                    Track your attendance across all classes and view detailed insights
                                </p>
                            </div>
                            <button
                                onClick={fetchAttendanceData}
                                className="flex items-center gap-2 bg-[#D67C2C] text-white px-4 py-2 rounded-lg hover:bg-[#B86A24] transition-colors font-[poppins]"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <div className="flex items-center gap-4 mb-4">
                            <Filter className="w-5 h-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-900 font-[poppins]">Filters</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-[poppins]">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent font-[poppins]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-[poppins]">
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent font-[poppins]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 font-[poppins]">
                                    Class
                                </label>
                                <select
                                    value={filters.classId}
                                    onChange={(e) => handleFilterChange('classId', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent font-[poppins]"
                                >
                                    <option value="">All Classes</option>
                                    {classesData.map((cls, index) => (
                                        <option key={index} value={cls.class_id}>
                                            {cls.class_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={clearFilters}
                                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-[poppins]"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* View Mode Selector */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <div className="flex items-center gap-4">
                            <Eye className="w-5 h-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-900 font-[poppins]">View Mode</h3>
                        </div>
                        <div className="flex gap-2 mt-4">
                            {[
                                { key: 'calendar', label: 'Calendar', icon: Calendar },
                                { key: 'list', label: 'List', icon: BookOpen },
                                { key: 'summary', label: 'Summary', icon: BarChart3 }
                            ].map(({ key, label, icon: Icon }) => (
                                <button
                                    key={key}
                                    onClick={() => setViewMode(key)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-[poppins] ${viewMode === key
                                        ? 'bg-[#D67C2C] text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content based on view mode */}
                    {viewMode === 'calendar' && renderCalendarView()}
                    {viewMode === 'list' && renderListView()}
                    {viewMode === 'summary' && renderSummaryView()}
                </div>
            </div>
        </Layout>
    );
};

export default MyAttendance;